import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Users, Calendar } from "lucide-react";
import { toast } from "sonner";

const WEEKDAYS = [
  { idx: 0, short: "Mon" },
  { idx: 1, short: "Tue" },
  { idx: 2, short: "Wed" },
  { idx: 3, short: "Thu" },
  { idx: 4, short: "Fri" },
  { idx: 5, short: "Sat" },
  { idx: 6, short: "Sun" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (iso, days) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function BulkShiftAssignDialog({ open, onOpenChange, users, templates, defaultDateFrom, defaultDateTo, onDone }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filter, setFilter] = useState("");
  const [type, setType] = useState(templates[0]?.key || "morning");
  const [startTime, setStartTime] = useState(templates[0]?.start_time || "09:00");
  const [endTime, setEndTime] = useState(templates[0]?.end_time || "17:00");
  const [dateFrom, setDateFrom] = useState(defaultDateFrom || todayISO());
  const [dateTo, setDateTo] = useState(defaultDateTo || addDaysISO(defaultDateFrom || todayISO(), 6));
  const [weekdays, setWeekdays] = useState(new Set(WEEKDAYS.map((w) => w.idx)));
  const [overwrite, setOverwrite] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setFilter("");
      setOverwrite(false);
      setNote("");
      setDateFrom(defaultDateFrom || todayISO());
      setDateTo(defaultDateTo || addDaysISO(defaultDateFrom || todayISO(), 6));
      setWeekdays(new Set(WEEKDAYS.map((w) => w.idx)));
      if (templates[0]) {
        setType(templates[0].key);
        setStartTime(templates[0].start_time);
        setEndTime(templates[0].end_time);
      }
    }
  }, [open, defaultDateFrom, defaultDateTo, templates]);

  const filteredUsers = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return users;
    return users.filter((u) =>
      [u.name, u.employee_id, u.role, u.department, u.email]
        .filter(Boolean)
        .some((x) => x.toLowerCase().includes(f))
    );
  }, [users, filter]);

  const allFilteredSelected = filteredUsers.length > 0
    && filteredUsers.every((u) => selectedIds.has(u.id));

  const toggleAllFiltered = () => {
    const next = new Set(selectedIds);
    if (allFilteredSelected) {
      filteredUsers.forEach((u) => next.delete(u.id));
    } else {
      filteredUsers.forEach((u) => next.add(u.id));
    }
    setSelectedIds(next);
  };

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleWeekday = (idx) => {
    const next = new Set(weekdays);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    setWeekdays(next);
  };

  const applyTemplate = (key) => {
    setType(key);
    const tpl = templates.find((t) => t.key === key);
    if (tpl) {
      setStartTime(tpl.start_time);
      setEndTime(tpl.end_time);
    }
  };

  const submit = async () => {
    if (selectedIds.size === 0) { toast.error("Select at least one employee"); return; }
    if (weekdays.size === 0) { toast.error("Select at least one weekday"); return; }
    if (!dateFrom || !dateTo) { toast.error("Pick a date range"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/shifts/bulk", {
        user_ids: Array.from(selectedIds),
        date_from: dateFrom,
        date_to: dateTo,
        weekdays: Array.from(weekdays),
        type,
        start_time: startTime,
        end_time: endTime,
        note: note || null,
        overwrite_existing: overwrite,
      });
      const parts = [
        `${data.created} created`,
        `${data.updated} updated`,
        data.skipped > 0 ? `${data.skipped} skipped` : null,
      ].filter(Boolean);
      toast.success(`Shifts bulk-assigned · ${parts.join(" · ")}`);
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} employee(s) had issues — see console`, { duration: 5000 });
        // eslint-disable-next-line no-console
        console.warn("Bulk shift errors:", data.errors);
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk assignment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="bulk-shift-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" /> Bulk Assign Shift
          </DialogTitle>
          <DialogDescription>
            Pick employees and a date range, then apply the same shift to all of them in one click.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* LEFT — Employees */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Employees ({selectedIds.size} selected)</Label>
              <button
                type="button"
                onClick={toggleAllFiltered}
                className="text-[11px] text-zinc-500 hover:text-zinc-900 underline"
                data-testid="bulk-shift-select-all"
              >
                {allFilteredSelected ? "Clear all" : "Select all visible"}
              </button>
            </div>
            <Input
              placeholder="Search name, role, department…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="bulk-shift-search"
            />
            <div className="border border-zinc-200 rounded-md max-h-64 overflow-y-auto divide-y divide-zinc-100">
              {filteredUsers.map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 cursor-pointer"
                  data-testid={`bulk-shift-user-${u.id}`}
                >
                  <Checkbox
                    checked={selectedIds.has(u.id)}
                    onCheckedChange={() => toggleOne(u.id)}
                    data-testid={`bulk-shift-user-check-${u.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{u.name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono truncate">
                      {u.employee_id || "—"} · {u.role.replace("_", " ")}
                      {u.department ? ` · ${u.department}` : ""}
                    </div>
                  </div>
                </label>
              ))}
              {filteredUsers.length === 0 && (
                <div className="py-6 text-center text-xs text-zinc-400">No matching staff.</div>
              )}
            </div>
          </div>

          {/* RIGHT — Shift + Dates */}
          <div className="space-y-3">
            <div>
              <Label>Shift template</Label>
              <Select value={type} onValueChange={applyTemplate}>
                <SelectTrigger data-testid="bulk-shift-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.key} value={t.key}>
                      {t.name} ({t.start_time}–{t.end_time})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label>Start</Label>
                <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="bulk-shift-start" />
              </div>
              <div>
                <Label>End</Label>
                <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="bulk-shift-end" />
              </div>
            </div>
            <div className="border-t border-zinc-200 pt-3">
              <Label className="flex items-center gap-1 text-sm font-semibold">
                <Calendar className="w-3.5 h-3.5" /> Date range
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                <div>
                  <Label className="text-[10px] text-zinc-500">From</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="bulk-shift-date-from" />
                </div>
                <div>
                  <Label className="text-[10px] text-zinc-500">To</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="bulk-shift-date-to" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold">Apply on days</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {WEEKDAYS.map((w) => {
                  const on = weekdays.has(w.idx);
                  return (
                    <button
                      key={w.idx}
                      type="button"
                      onClick={() => toggleWeekday(w.idx)}
                      className={`px-2 py-1 rounded text-[11px] font-semibold uppercase tracking-wider border transition-colors ${
                        on
                          ? "bg-zinc-900 text-white border-zinc-900"
                          : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400"
                      }`}
                      data-testid={`bulk-shift-wday-${w.idx}`}
                    >
                      {w.short}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. festival week roster" data-testid="bulk-shift-note" />
            </div>
            <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-md p-2">
              <div>
                <Label className="text-sm font-semibold">Overwrite existing shifts</Label>
                <p className="text-[11px] text-zinc-500 leading-tight">
                  If off, days that already have a shift assigned are skipped.
                </p>
              </div>
              <Switch checked={overwrite} onCheckedChange={setOverwrite} data-testid="bulk-shift-overwrite" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={submit}
            disabled={busy || selectedIds.size === 0}
            className="bg-zinc-950 hover:bg-zinc-800"
            data-testid="bulk-shift-save"
          >
            {busy ? "Assigning…" : `Assign to ${selectedIds.size || "—"} employee${selectedIds.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
